/**
 * records.js
 */

import { hookstate, useHookstate, none } from '@hookstate/core';
import { 
  parseISO, 
  format,
} from 'date-fns';
import {
  Types,
  RECORD_QUERY_PAGE_BUFFER
} from 'utils/constants';
import { binarySearchPosition } from 'utils/binary-search';
import logger from 'utils/logger';


const recordList = hookstate({
  all: [],
  visible: [],

  recordCountTotal: -1,
  pageCountTotal: -1, // internally calculated
  pageCountLocal: -1, // internally calculated
  pageNum: 1,
  pageSize: 10, // currently constant
  possiblyOutdated: false,
  isLoading: false,
});


export default function useRecordList() {
  const state = useHookstate(recordList);


  /**
   * calculates local and total page counts
   */
  const calculatePageCounts = (s = state) => {
    s.pageCountTotal.set(Math.ceil(s.recordCountTotal.get() / s.pageSize.get()));
    s.pageCountLocal.set(s.all.get().length / s.pageSize.get());
  }


  /**
   * converts a list of records into a flat list of record-list-format records
   */
  const flattenRecords = (records) => {
    const formatDate = (date) => format(parseISO(date), 'yyyy-MM-dd HH:mm');
    let flatList = [];

    records.forEach(record => {
      if (record) {
        // EVENT
        if (record.recordType === Types.Record.EVENT) {
          if (record.exercises && record.exercises.length > 0) {
            // EXERCISES
            record.exercises.forEach(item => {
              const exerciseId = typeof(item) === 'object' ? item.id : item;
              flatList.push({
                date: formatDate(record.date), 
                eventId: record.id,
                type: Types.Record.EXERCISE, 
                groupType: Types.RecordGroup.TRAINING,
                id: exerciseId,
              });
            });
          }
          else {
            // EMPTY EVENT
            flatList.push({
              date: formatDate(record.date), 
              eventId: record.id,
              type: Types.Record.EVENT,
              groupType: Types.RecordGroup.TRAINING,
              id: record.id
            });
          }
        }
        // DEPLOYMENT
        else if (record.recordType === Types.Record.DEPLOYMENT) {
          flatList.push({
            date: formatDate(record.date), 
            eventId: record.id,
            type: Types.Record.DEPLOYMENT, 
            groupType: Types.RecordGroup.DEPLOYMENT,
            id: record.id
          });
        }
        // CLASS
        else if (record.recordType === Types.Record.CLASS) {
          flatList.push({
            date: formatDate(record.date), 
            eventId: record.id,
            type: Types.Record.CLASS, 
            groupType: Types.RecordGroup.CLASS,
            id: record.id
          });
        }
        // VET VISIT
        else if (record.recordType === Types.Record.VET_VISIT) {
          flatList.push({
            date: formatDate(record.date),
            eventId: record.id,
            type: Types.Record.VET_VISIT,
            groupType: Types.RecordGroup.VET_VISIT,
            id: record.id
          });
        }
      }
    });

    return flatList;
  }


  /**
   * adds the provided list of records to the list of all local records.
   * this does NOT update the record list meta data.
   */
  const addRecords = (records, s = state) => {
    const insertList = flattenRecords(records);
    s.all.merge(insertList);
    return insertList.length;
  }


  /**
   * removes given records from record list (if found) and returns the number removed
   */
  const removeRecords = (records, s = state) => {
    let numRemoved = 0;
    const removeList = flattenRecords(records);
    removeList.forEach(record => {
      const pos = binarySearchPosition(s.all.get(), record, comparator);
      if (pos.exact !== null) {
        s.all[pos.exact].set(none);
        numRemoved++;
      }
    });

    return numRemoved;
  }


  /**
   * updates the visible records based on page number
   */
  const updateVisibleRecords = async (s = state) => {
    const pageNum = s.pageNum.get();
    const pageSize = s.pageSize.get();
    const pageCountTotal = s.pageCountTotal.get();
    const pageCountLocal = s.pageCountLocal.get();
    const startIndex = (pageNum - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const isPageCached = pageNum <= pageCountLocal || pageNum === pageCountTotal;

    if (pageCountTotal > 0 && !isPageCached) {
      s.isLoading.set(true);
    } else {
      s.isLoading.set(false);
    }

    if (pageCountLocal === 0) {
      s.visible.set([]);
    } else if (isPageCached) {
      const subset = s.all.get().slice(startIndex, endIndex);
      s.visible.set(subset);
    }
  }


  /**
   * record list sort comparator
   * - descending Event Date
   * - descending Event ID 
   * - descending Exercise ID
   * - ascending Handler last name - NOT YET IMPLEMENTED
   */
  const comparator = (a, b) => {
    const aDate = parseISO(a.date);
    const bDate = parseISO(b.date);

    // event date desc
    if (aDate > bDate) {
      return -1;
    } else if (aDate < bDate) {
      return 1;
    } 
    // event id desc
    else if (a.eventId > b.eventId) {
      return -1;
    } else if (a.eventId < b.eventId) {
      return 1;
    }
    // exercise id desc
    else if (a.id > b.id) {
      return -1;
    } else if (a.id < b.id) {
      return 1;
    }

    return 0;
  }


  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // RECORD LIST INTERFACE
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------

  return ({

    /**
     * reset record list. 
     * pageNum can be initialized explicitly or pass false to leave it untouched.
     */
    reset: (pageNum = 1) => {
      const newPageNum = pageNum === false ? state.pageNum.get() : pageNum;
      state.set({
        all: [],
        visible: [],
  
        recordCountTotal: -1,
        pageCountTotal: -1,
        pageCountLocal: -1,
        pageNum: newPageNum,
        pageSize: 10,
        possiblyOutdated: false,
        isLoading: false
      });
    },


    /**
     * process a chunk of records. this assumes that records are already sorted and are appended to the list.
     * this also receives the total number of records as it gets returned by the server and can be set here.
     * recordCountTotal: 
     * - number: uses number for the total
     * - null: does nothing
     * - undefined: uses the calculated number added as the total
     */
    processChunk: (records, recordCountTotal) => {
      const numAdded = addRecords(records, state);
      if (typeof recordCountTotal === 'number') {
        state.recordCountTotal.set(recordCountTotal);
      } else if (recordCountTotal === undefined) {
        state.recordCountTotal.set(numAdded);
      }
      calculatePageCounts(state);
      updateVisibleRecords(state);

      logger.log(`[record-list.js] Chunk processing complete. Record list contains ${state.all.get().length}`
        + `/${state.recordCountTotal.get()} records. `);
  },


    /**
     * add records to list, sort and update the list counts
     */
    add: (records) => {
      const numAdded = addRecords(records, state);
      state.all.set(p => [...p].sort(comparator));
      state.recordCountTotal.set(p => p + numAdded);
      calculatePageCounts(state);
      updateVisibleRecords(state);
    },


    /**
     * this replaces the records in the list with the given records.
     * we remove all relevant records and re-add the provided ones, sort and update the list counts.
     */
    update: (records) => {
      const recordCountLocalBefore = state.all.get().length;
      
      // remove all relevant records
      records.forEach(record => {
        if (record.recordType === Types.Record.EVENT) {
          state.all.set(p => p.filter(r => 
            !(
              (r.type === Types.Record.EVENT || r.type === Types.Record.EXERCISE)
              && r.eventId === record.id
            )))
        } else if (record.recordType === Types.Record.DEPLOYMENT) {
          state.all.set(p => p.filter(r => 
            !(r.type === Types.Record.DEPLOYMENT && r.eventId === record.id)))
        } else if (record.recordType === Types.Record.CLASS) {
          state.all.set(p => p.filter(r => 
            !(r.type === Types.Record.CLASS && r.eventId === record.id)))
        } else if (record.recordType === Types.Record.VET_VISIT) {
          state.all.set(p => p.filter(r =>
            !(r.type === Types.Record.VET_VISIT && r.eventId === record.id)))
        }
      });

      // add and sort updated records
      addRecords(records, state);
      state.all.set(p => [...p].sort(comparator));

      // update total
      const numDiff = state.all.get().length - recordCountLocalBefore;
      state.recordCountTotal.set(p => p + numDiff);

      calculatePageCounts(state);
      updateVisibleRecords(state);
    },


    /**
     * removes records from the record list, updating the list counts.
     */
    remove: (records) => {
      const numRemoved = removeRecords(records, state);
      state.recordCountTotal.set(p => p - numRemoved);
      calculatePageCounts(state);
      updateVisibleRecords(state);
    },


    /**
     * sets the current page number.
     * this will update the visible records list and return whether more need to be loaded.
     */
    setPageNum: (pageNum) => {
      // determine if we need to load more records
      const pageCountTotal = state.pageCountTotal.get();
      const pageCountLocal = state.pageCountLocal.get();
      const numTotal = state.recordCountTotal.get();
      const numLocal = state.all.get().length;
      const loadMoreRecords = 
        pageCountTotal > 0
        && pageNum > pageCountLocal - RECORD_QUERY_PAGE_BUFFER
        && (
          Math.ceil(pageCountLocal) < pageCountTotal
          || numLocal < numTotal
        );

      if (pageNum !== state.pageNum.get()) {
        state.pageNum.set(pageNum);
        updateVisibleRecords(state);
      }

      return loadMoreRecords;
    },


    /**
     * gets the current page num
     */
    getPageNum: () => {
      return state.pageNum.get();
    },


    /**
     * gets the current page size (currently static)
     */
    getPageSize: () => {
      return state.pageSize.get();
    },


    /**
     * sets the possibly outdated flag
     */
    setPossiblyOutdated: (val) => {
      state.possiblyOutdated.set(val);
    },


    /**
     * record list state: visible records, page number and page count
     */
    useState: () => {
      return {
        records: state.visible.get(),
        pageNum: state.pageNum.get(),
        pageCount: state.pageCountTotal.get(),
        possiblyOutdated: state.possiblyOutdated.get(),
        recordCountTotal: state.recordCountTotal.get(),
        isLoading: state.isLoading.get()
      };
    },
  });
}